import { useEffect, useState } from "react";
import type { ListViewStatus } from "@carbon6/c6-frontend-design";

export type AsyncListState<T> = {
  items: readonly T[];
  status: ListViewStatus;
  errorMessage: string | null;
  reload: () => void;
};

// 팀 스코프 training read 패널이 함께 쓰는 1회 fetch 상태. 호출부는 fetcher 를 useCallback 으로
// 안정화한다(의존값이 바뀌면 새 함수가 되어 다시 요청한다). reload 로 수동으로 다시 요청한다.
export function useAsyncList<T>(fetcher: () => Promise<readonly T[]>): AsyncListState<T> {
  const [items, setItems] = useState<readonly T[]>([]);
  const [status, setStatus] = useState<ListViewStatus>("loading");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  // fetcher 를 객체에 담아 둔다. 함수 그대로 두면 setState 가 그 함수를 updater 로 받아 렌더 중에
  // 요청을 보낸다.
  const [settled, setSettled] = useState({ fetcher });

  // 무효화를 아래 effect 에 맡기면 그 한 flush 동안 status 가 아직 success 다. 그러면 호출부는 이전
  // 질의의 목록을 현재 질의의 응답으로 읽는다. 딥링크 정리 effect 가 그 값을 근거로 정상 선택을
  // 지운다.
  if (settled.fetcher !== fetcher) {
    setSettled({ fetcher });
    setItems([]);
    setStatus("loading");
    setErrorMessage(null);
  }

  useEffect(() => {
    let disposed = false;
    setStatus("loading");
    setErrorMessage(null);
    fetcher()
      .then((data) => {
        if (!disposed) {
          setItems(data);
          setStatus("success");
        }
      })
      .catch((error: unknown) => {
        if (!disposed) {
          setErrorMessage(resolveErrorMessage(error));
          setStatus("error");
        }
      });
    return () => {
      disposed = true;
    };
  }, [fetcher, reloadKey]);

  return { items, status, errorMessage, reload: () => setReloadKey((key) => key + 1) };
}

function resolveErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return "training 정보를 불러오지 못했습니다.";
}
